import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, navigate } from 'gatsby';
import styled from 'styled-components';
import Branded from '../components/Branded';

const Section = styled.section`
  background: #fff;
  padding: 100px 0 80px;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 64px 0 48px;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Top = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 48px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 24px;
    margin-bottom: 32px;
  }
`;

const TitleBlock = styled.div`
  .tag {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #f08020;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 10px;

    &::before {
      content: '';
      width: 24px;
      height: 1px;
      background: #f08020;
    }
  }

  h2 {
    font-size: 2.4rem;
    font-weight: 700;
    color: #111;
    line-height: 1.2;
    margin-bottom: 12px;

    @media (max-width: 768px) {
      font-size: 1.7rem;
    }
  }

  .subtitle {
    font-size: 14px;
    color: #5b6b7c;
    line-height: 1.6;
    max-width: 400px;
  }
`;

const ProjectCount = styled.div`
  text-align: right;

  @media (max-width: 768px) {
    text-align: left;
  }

  .count-num {
    font-size: 3.5rem;
    font-weight: 700;
    color: #f08020;
    line-height: 1;

    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }

  .count-label {
    font-size: 12px;
    color: #5b6b7c;
    letter-spacing: 0.08em;
    margin-top: 4px;
  }
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 32px;
`;

const FilterBtn = styled.button`
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  padding: 8px 20px;
  border: 1px solid ${({ active }) => (active ? '#1378E5' : '#D8E1EC')};
  background: ${({ active }) => (active ? '#1378E5' : 'transparent')};
  color: ${({ active }) => (active ? '#fff' : '#5B6B7C')};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #1378e5;
    color: ${({ active }) => (active ? '#fff' : '#1378E5')};
  }
`;

const ScrollWrap = styled.div`
  overflow-x: scroll;
  overflow-y: hidden;
  position: relative;
  cursor: grab;
  user-select: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &:active {
    cursor: grabbing;
  }
`;

const ScrollTrack = styled.div`
  display: flex;
  gap: 12px;
  width: max-content;
`;

const Card = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  cursor: pointer;
  width: 380px;
  height: 260px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 300px;
    height: 210px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .card-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, transparent 50%);
    z-index: 1;
  }

  .card-info {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 18px 20px;
    z-index: 2;
  }

  .card-cat {
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: #f08020;
    margin-bottom: 4px;
  }

  .card-title {
    font-size: 14px;
    font-weight: 500;
    color: #fff;
    line-height: 1.3;
  }

  &:hover img {
    transform: scale(1.06);
  }
`;

const CtaWrap = styled.div`
  text-align: center;
  margin-top: 48px;

  a {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #1378e5;
    padding: 13px 32px;
    border: 1px solid #1378e5;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);

    svg {
      width: 14px;
      height: 14px;
      stroke: #1378e5;
      fill: none;
      stroke-width: 1.5;
      stroke-linecap: round;
      stroke-linejoin: round;
      transition: transform 0.3s ease, stroke 0.3s ease;
    }

    &:hover {
      background: #1378e5;
      color: #fff;
      svg {
        stroke: #fff;
        transform: translateX(4px);
      }
    }
  }
`;

export default function FeaturedProjects({
  tag,
  title,
  subtitle,
  projects,
  categories,
  totalCount,
}) {
  const [activeFilter, setActiveFilter] = useState(null);
  const [needsLoop, setNeedsLoop] = useState(false);
  const scrollRef = useRef(null);
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const autoSpeed = useRef(1);
  const rafId = useRef(null);
  const allProjects = projects || [];
  const allCategories = categories || [];

  const filtered = activeFilter
    ? allProjects.filter(p => p.category?._id === activeFilter)
    : allProjects;

  const items = needsLoop ? [...filtered, ...filtered, ...filtered] : filtered;

  // Detect whether content overflows the viewport (only then loop/duplicate)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const check = () => {
      const cardWidth = window.innerWidth <= 768 ? 312 : 392; // card + gap
      setNeedsLoop(filtered.length * cardWidth > el.clientWidth);
    };

    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [filtered.length]);

  // Auto scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !needsLoop || filtered.length < 2) return;

    const tick = () => {
      if (!isDragging.current) {
        el.scrollLeft += autoSpeed.current;

        // Reset to middle copy for seamless loop
        const oneSet = el.scrollWidth / 3;
        if (el.scrollLeft >= oneSet * 2) {
          el.scrollLeft -= oneSet;
        }
        if (el.scrollLeft <= 0) {
          el.scrollLeft += oneSet;
        }
      }
      rafId.current = requestAnimationFrame(tick);
    };

    // Start at first copy
    el.scrollLeft = el.scrollWidth / 3;
    rafId.current = requestAnimationFrame(tick);

    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [filtered.length, activeFilter, needsLoop]);

  // Drag handlers
  const onMouseDown = useCallback(e => {
    isDragging.current = true;
    hasDragged.current = false;
    startX.current = e.pageX;
    scrollLeft.current = scrollRef.current.scrollLeft;
  }, []);

  const onMouseMove = useCallback(e => {
    if (!isDragging.current) return;
    e.preventDefault();
    const dx = e.pageX - startX.current;
    if (Math.abs(dx) > 5) hasDragged.current = true;
    scrollRef.current.scrollLeft = scrollLeft.current - dx;
  }, []);

  const onMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  const onTouchStart = useCallback(e => {
    isDragging.current = true;
    hasDragged.current = false;
    startX.current = e.touches[0].pageX;
    scrollLeft.current = scrollRef.current.scrollLeft;
  }, []);

  const onTouchMove = useCallback(e => {
    if (!isDragging.current) return;
    const dx = e.touches[0].pageX - startX.current;
    if (Math.abs(dx) > 5) hasDragged.current = true;
    scrollRef.current.scrollLeft = scrollLeft.current - dx;
  }, []);

  const onTouchEnd = useCallback(() => {
    isDragging.current = false;
  }, []);

  useEffect(() => {
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchend', onTouchEnd);
    return () => {
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [onMouseUp, onTouchEnd]);

  if (!allProjects.length) return null;

  return (
    <Section>
      <Container>
        <Top>
          <TitleBlock>
            {tag && <div className="tag">{tag}</div>}
            <h2>{title || 'Featured Projects'}</h2>
            <Branded as="p" className="subtitle" text={subtitle} />
          </TitleBlock>
          <ProjectCount>
            <div className="count-num">{totalCount || allProjects.length}+</div>
            <div className="count-label">Projects Completed</div>
          </ProjectCount>
        </Top>

        {allCategories.length > 0 && (
          <Filters>
            <FilterBtn
              active={!activeFilter}
              onClick={() => setActiveFilter(null)}
            >
              All
            </FilterBtn>
            {allCategories.map(cat => (
              <FilterBtn
                key={cat._id}
                active={activeFilter === cat._id}
                onClick={() => setActiveFilter(cat._id)}
              >
                {cat.name}
              </FilterBtn>
            ))}
          </Filters>
        )}

        <ScrollWrap
          ref={scrollRef}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
        >
          <ScrollTrack key={activeFilter}>
            {items.map((project, idx) => (
              <Card
                key={`${project._id}-${idx}`}
                onClick={() => {
                  if (!hasDragged.current)
                    navigate(`/portfolio/${project.slug?.current}`);
                }}
              >
                {project.coverImage?.asset?.url ? (
                  <img
                    src={project.coverImage.asset.url + '?w=500&auto=format'}
                    alt={project.title}
                    draggable="false"
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      background: '#F2F6FC',
                    }}
                  />
                )}
                <div className="card-overlay" />
                <div className="card-info">
                  {project.category?.name && (
                    <div className="card-cat">{project.category.name}</div>
                  )}
                  <div className="card-title">{project.title}</div>
                </div>
              </Card>
            ))}
          </ScrollTrack>
        </ScrollWrap>

        <CtaWrap>
          <Link to="/portfolio">
            View All Projects
            <svg viewBox="0 0 24 24">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </CtaWrap>
      </Container>
    </Section>
  );
}
